import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff } from "lucide-react";
import portalApi from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import "@/styles/crm.css";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_d216ca66-0733-41eb-aefb-a68564ba10f0/artifacts/fn20k37z_FINAL%20Logo%20Newsky%20png.png";

export default function PortalSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = usePortalAuth();
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    portalApi.get(`/portal/validate-token/${token}`)
      .then((r) => { setInfo(r.data); setValidating(false); })
      .catch(() => { setError("Link inválido ou expirado."); setValidating(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (password.length < 6) { setError("Senha deve ter no mínimo 6 caracteres"); return; }
    if (password !== confirm) { setError("As senhas não coincidem"); return; }
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/set-password", { token, password });
      if (data.access_token) localStorage.setItem("portal_access_token", data.access_token);
      await checkAuth();
      navigate("/portal");
    } catch (err) { setError(err.response?.data?.detail || "Erro ao configurar senha"); }
    setLoading(false);
  };

  if (validating) return <div className="crm-page min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--crm-accent)" }} /></div>;

  return (
    <div className="crm-page min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="NewSky" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold" style={{ color: "var(--crm-text)" }}>Ativar Portal</h1>
          {info && <p className="text-sm mt-1" style={{ color: "var(--crm-text-muted)" }}>Olá, {info.name}! Crie sua senha de acesso.</p>}
        </div>

        <form onSubmit={handleSubmit} className="crm-card p-6 space-y-4" data-testid="portal-setup-form">
          {error && <div className="p-3 rounded-xl text-sm" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", color: "#ef4444" }}>{error}</div>}
          <div>
            <label className="crm-label">Nova Senha</label>
            <div className="relative">
              <input type={showPass ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-setup-password" className="crm-input pr-10" placeholder="Mínimo 6 caracteres" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1" style={{ color: "var(--crm-text-dim)" }}>
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="crm-label">Confirmar Senha</label>
            <div className="relative">
              <input type={showPass ? "text" : "password"} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="portal-setup-confirm" className="crm-input pr-10" placeholder="Repita a senha" />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1" style={{ color: "var(--crm-text-dim)" }}>
                {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="portal-setup-submit" className="crm-btn-primary w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Ativar Minha Conta
          </button>
        </form>
      </div>
    </div>
  );
}
